import { useCallback, useRef, useState } from "react";
import Link from "next/link";
import { ROUTE } from "@/route/app_routes.js";

const MARGIN = 10;
const WIDTH = 320;

// Tracks which course card is currently hovered and where its tooltip should float. Renders
// via `position: fixed` computed from the hovered element's real bounding box -- rendering the
// tooltip as a child of the card would get clipped, since `.rbt-course-card` uses
// `overflow: hidden` for its rounded thumbnail corners, and its `:hover` transform also creates
// a new containing block that would defeat `position: fixed` escaping the clip anyway.
// A short, deliberate "hover intent" delay before the tooltip appears -- showing it
// instantly on every pass of the cursor reads as flickery/abrupt rather than sleek; a brief
// pause confirms the visitor actually paused on the card before committing to the reveal.
const SHOW_DELAY_MS = 220;

export function useCourseHoverTooltip() {
  const [tooltip, setTooltip] = useState(null); // { course, rect }
  const hideTimer = useRef(null);
  const showTimer = useRef(null);

  const show = useCallback((course, targetEl) => {
    clearTimeout(hideTimer.current);
    clearTimeout(showTimer.current);
    if (!targetEl) return;
    showTimer.current = setTimeout(() => {
      setTooltip({ course, rect: targetEl.getBoundingClientRect() });
    }, SHOW_DELAY_MS);
  }, []);

  const hide = useCallback(() => {
    clearTimeout(showTimer.current);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setTooltip(null), 90);
  }, []);

  const cancelHide = useCallback(() => clearTimeout(hideTimer.current), []);

  return { tooltip, show, hide, cancelHide };
}

const ESTIMATED_HEIGHT = 230;

const CourseHoverTooltip = ({ tooltip, onMouseEnter, onMouseLeave }) => {
  if (!tooltip) return null;
  const { course, rect } = tooltip;

  // Same URL the card itself links to (see FeaturedCourses.js / TrendingCourses.js --
  // both build their <Link href> as `${ROUTE.course_details}${course.id}`) so the CTA
  // navigates to the exact same course-details page a click on the card would.
  const courseDetailsUrl = `${ROUTE.course_details}${course.id}`;

  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 900;

  // Prefer the card's right side; flip to its left side only if there's no room to the right.
  const openLeft = rect.right + MARGIN + WIDTH > viewportWidth;
  const left = openLeft ? Math.max(MARGIN, rect.left - WIDTH - MARGIN) : rect.right + MARGIN;

  let top = rect.top;
  if (top + ESTIMATED_HEIGHT > viewportHeight - MARGIN) top = viewportHeight - ESTIMATED_HEIGHT - MARGIN;
  if (top < MARGIN) top = MARGIN;

  return (
    <div
      // Keyed by course id so switching cards quickly REMOUNTS this element (rather than
      // React reusing the same DOM node and just patching its content) -- a CSS `animation`
      // only plays on mount, so without this key the entrance animation only ever played
      // once and every subsequent card just snapped straight to its end state.
      key={course.id}
      className="rbt-course-tooltip"
      style={{ position: "fixed", left, top, width: WIDTH }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Skill-level badge sits on its own row ABOVE the title (a plain flex-column stack,
          `.rbt-course-tooltip__head`), not absolutely positioned over/beside it. An earlier
          attempt pinned the badge as an absolutely-positioned corner pill and reserved space
          for it via `padding-right` on the title -- that only holds for short badge text
          ("Beginner"/"Advanced"); "Intermediate" renders ~30-40px wider than either (longer
          word, uppercase, letter-spacing) and blew past the reserved padding, so it still
          overlapped the title. Stacking them in normal flow means the badge always gets
          exactly the row height it needs and the title always starts clear below it,
          regardless of badge text length or how many lines the title wraps to. */}
      <div className="rbt-course-tooltip__head">
        {course.skill_level && <span className="rbt-course-tooltip__level">{course.skill_level}</span>}
        <strong>{course.title}</strong>
      </div>

      {(course.short_description || course.min_description) && (
        <p className="rbt-course-tooltip__desc">{course.short_description || course.min_description}</p>
      )}

      <div className="rbt-course-tooltip__meta">
        {(course.modules_count || course.modules) && (
          <span><i className="feather-layers"></i>{course.modules_count || course.modules} modules</span>
        )}
        {(course.lessons_count || course.lessons) && (
          <span><i className="feather-play-circle"></i>{course.lessons_count || course.lessons} lessons</span>
        )}
        {(course.students || course.students === 0) && (
          <span><i className="feather-users"></i>{course.students} enrolled</span>
        )}
      </div>

      <Link href={courseDetailsUrl} className="rbt-course-tooltip__cta">
        View course <i className="feather-arrow-right"></i>
      </Link>
    </div>
  );
};

export default CourseHoverTooltip;
